import type { CostPriceAnalysis, CostPriceIngredient } from '../../../../services/costPrice/costPriceService';
import type { CostPriceCategory } from '../../../../services/costPrice/costPriceCategoriesService';
import type { CustomIngredient } from '../../../../services/customIngredients/customIngredientsService';
import type { Subrecipe } from '../../../../services/subrecipes/subrecipesService';

export const isDishDone = (dish: CostPriceAnalysis) => !!dish.automationReviewedAt || !!dish.markedIrrelevant;
export const runDishes = (runId: string, dishes: CostPriceAnalysis[]) => dishes.filter(d => d.automationRunId === runId);
export const runProgress = (dishes: CostPriceAnalysis[]) => ({ done: dishes.filter(isDishDone).length, total: dishes.length });
export const canMarkComplete = (dish: CostPriceAnalysis) => dish.ingredients.length > 0;
export const dishMissingQuantity = (dish: CostPriceAnalysis) => dish.ingredients.some(row =>
  !row.nestedCostPriceId && !((row.subrecipeId ? row.subrecipePortionsNeeded : row.quantityNeeded)! > 0));

export function categoryProgress(dishes: CostPriceAnalysis[], categories: CostPriceCategory[], uncategorizedName = '') {
  const known = new Set(categories.map(category => category.id));
  const hasUncategorized = dishes.some(dish => !dish.categoryId || !known.has(dish.categoryId));
  const available = hasUncategorized ? [...categories, {
    id: 'uncategorized', restaurantId: dishes[0]?.restaurantId ?? '', name: uncategorizedName,
    color: '', position: Number.MAX_SAFE_INTEGER, scope: 'revenue' as const,
    createdAt: new Date(0), updatedAt: new Date(0),
  }] : categories;
  return available.slice().sort((a, b) => a.position - b.position || a.name.localeCompare(b.name)).map(category => {
    const grouped = dishes.filter(d => category.id === 'uncategorized' ? !d.categoryId || !known.has(d.categoryId) : d.categoryId === category.id).sort((a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() || a.costPriceName.localeCompare(b.costPriceName));
    return { categoryId: category.id, category, dishes: grouped, ...runProgress(grouped),
      ingredientLineCount: grouped.reduce((sum, dish) => sum + dish.ingredients.length, 0) };
  }).filter(group => group.total > 0);
}

export type RunSubrecipe = {
  subrecipeId: string;
  subrecipe: Subrecipe | undefined;
  status: 'pending' | 'complete';
  invalidDishReference?: { reason: 'missing' | 'cycle'; dishId: string; ownerDishId: string };
  usedIn: { dishName: string; amount: number; unit: string }[];
};

export function runSubrecipes(dishes: CostPriceAnalysis[], subrecipes: Map<string, Subrecipe>, allDishes: CostPriceAnalysis[] = dishes): RunSubrecipe[] {
  const result = new Map<string, RunSubrecipe>();
  const isComplete = (id: string, path = new Set<string>()): boolean => {
    const sub = subrecipes.get(id);
    if (!sub?.ingredients.length || path.has(id)) return false;
    const nextPath = new Set(path).add(id);
    return sub.ingredients.every(row => !row.nestedSubrecipeId || isComplete(row.nestedSubrecipeId, nextPath));
  };
  const visit = (id: string, dishName: string, amount: number, path = new Set<string>()) => {
    if (path.has(id)) return;
    const subrecipe = subrecipes.get(id);
    let entry = result.get(id);
    if (!entry) {
      entry = { subrecipeId: id, subrecipe, status: isComplete(id) ? 'complete' : 'pending', usedIn: [] };
      result.set(id, entry);
    }
    const usage = entry.usedIn.find(use => use.dishName === dishName);
    if (usage) usage.amount += amount;
    else entry.usedIn.push({ dishName, amount, unit: subrecipe?.portionsUnit ?? 'p' });
    const nextPath = new Set(path).add(id);
    subrecipe?.ingredients.forEach(row => {
      if (row.nestedSubrecipeId) visit(row.nestedSubrecipeId, dishName,
        amount * (row.nestedSubrecipePortionsNeeded ?? 0) / (subrecipe.portions > 0 ? subrecipe.portions : 1), nextPath);
    });
  };
  const dishMap = new Map(allDishes.map(dish => [dish.id, dish]));
  const invalidDish = (dishId: string, owner: CostPriceAnalysis, amount: number, reason: 'missing' | 'cycle') => {
    const id = `dish:${dishId}:${owner.id}`;
    if (!result.has(id)) result.set(id, {
      subrecipeId: id, subrecipe: undefined, status: 'pending',
      invalidDishReference: { reason, dishId, ownerDishId: owner.id },
      usedIn: [{ dishName: owner.costPriceName, amount, unit: 'p' }],
    });
  };
  const visitDish = (dish: CostPriceAnalysis, owner: CostPriceAnalysis, multiplier: number, path = new Set<string>()) => {
    if (path.has(dish.id)) { invalidDish(dish.id, owner, multiplier, 'cycle'); return; }
    const nextPath = new Set(path).add(dish.id);
    dish.ingredients.forEach(row => {
      if (row.subrecipeId) visit(row.subrecipeId, owner.costPriceName, (row.subrecipePortionsNeeded ?? 0) * multiplier);
      else if (row.nestedCostPriceId) {
        const nested = dishMap.get(row.nestedCostPriceId);
        const amount = (row.nestedQuantityNeeded ?? 0) * multiplier;
        if (nested) visitDish(nested, owner, amount, nextPath);
        else invalidDish(row.nestedCostPriceId, owner, amount, 'missing');
      }
    });
  };
  dishes.forEach(dish => visitDish(dish, dish, 1));
  return Array.from(result.values());
}

export function deriveSteps(_run: unknown, dishes: CostPriceAnalysis[], subs: RunSubrecipe[]) {
  const dishesDone = dishes.length > 0 && dishes.every(isDishDone);
  const subrecipesDone = dishesDone && subs.every(sub => sub.status === 'complete');
  return { activeStep: (!dishesDone ? 2 : !subrecipesDone ? 3 : 4) as 2 | 3 | 4,
    dishesDone, subrecipesDone, skipSubrecipes: dishesDone && subs.length === 0, canFinish: dishesDone && subrecipesDone && !dishes.some(dish => dish.automationReviewFlagged) };
}

export function reviewCustomIngredient(row: CostPriceIngredient, customIngredients: CustomIngredient[], restaurantId?: string) {
  if (!row.custom || row.subrecipeId || row.nestedCostPriceId) return undefined;
  return customIngredients.find(custom => custom.articleId === row.articleId && custom.platform === row.platform &&
    (!(row.sourceRestaurantId ?? restaurantId) || custom.restaurantId === (row.sourceRestaurantId ?? restaurantId)));
}

export function rowKind(row: CostPriceIngredient, customIngredients: CustomIngredient[], restaurantId?: string) {
  if (row.subrecipeId) return 'subrecipe';
  if (row.nestedCostPriceId) return 'nested';
  if (!row.custom) return 'order';
  const library = reviewCustomIngredient(row, customIngredients, restaurantId);
  return library?.price === 0 ? 'customUnpriced' : 'customPriced';
}

export function toSubrecipeRow(row: CostPriceIngredient, subrecipe: Subrecipe): CostPriceIngredient {
  if (row.quantityInfo?.stdUnit !== subrecipe.portionsUnit) throw new Error('unitMismatch');
  return { id: row.id, subrecipeId: subrecipe.id, subrecipePortionsNeeded: row.quantityNeeded,
    ...(row.automationTemplateKey && { automationTemplateKey: row.automationTemplateKey }),
    ...(row.wastePercentage !== undefined && { wastePercentage: row.wastePercentage }),
    ...(row.sourceRestaurantId && { sourceRestaurantId: row.sourceRestaurantId, sourceRestaurantName: row.sourceRestaurantName }) };
}

export function toNameOnlyRow(row: CostPriceIngredient, custom: CustomIngredient): CostPriceIngredient {
  const { subrecipeId, subrecipePortionsNeeded, subrecipeName, subrecipeTotalPortions, subrecipePortionsUnit,
    nestedCostPriceId, nestedCostPriceName, nestedQuantityNeeded, ...rest } = row;
  return { ...rest, articleId: custom.articleId, productName: custom.productName, platform: custom.platform,
    customPlatformName: custom.customPlatformName, quantityInfo: { ...custom.quantityInfo }, custom: true,
    quantityNeeded: subrecipeId ? subrecipePortionsNeeded : row.quantityNeeded };
}
